/**
 * Device release — the customer takes the device home while the job waits
 * (docs/device-release-awaiting-parts.md).
 *
 * Mirrors RELEASE_ELIGIBLE_STATUSES in sm4rt-api
 * `core/services/work_item_release_service.py` — change them together, or the
 * button renders and the release 409s.
 */

import type { WorkItem } from '../components/workitems/workItemTypes';
import { normalizeStatus } from '../components/workitems/utils/workItemUtils';

/** As slugs: a WorkItem's status arrives as `waiting-for-parts`, not the
 *  RepairShopr label — comparing labels hid the button on every real job. */
export const RELEASE_ELIGIBLE_STATUSES: ReadonlySet<string> = new Set([
  'waiting-for-parts',
  'parts-to-be-ordered',
  'waiting-on-supplier-for-quote',
]);

const statusOf = (item: Pick<WorkItem, 'status' | 'jobStatus'>): string =>
  normalizeStatus(item.jobStatus || item.status || '');

/** Offer "Release device": a job, in the shop, waiting on something. */
export function canReleaseDevice(item: WorkItem): boolean {
  return item.type === 'job' && !item.deviceWithCustomer && RELEASE_ELIGIBLE_STATUSES.has(statusOf(item));
}

/** Offer "Mark device returned": the device is out with the customer. */
export function canReturnDevice(item: WorkItem): boolean {
  return item.type === 'job' && !!item.deviceWithCustomer;
}

/** "Battery, ETA 2 weeks · since 20 Oct" — the chip's tooltip. */
export function deviceReleaseSummary(item: WorkItem): string {
  const parts: string[] = [];
  if (item.deviceReleaseReason) parts.push(item.deviceReleaseReason);
  if (item.deviceReleasedAt) {
    const d = new Date(item.deviceReleasedAt);
    if (!Number.isNaN(d.getTime())) {
      parts.push(
        `since ${d.toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short' })}`,
      );
    }
  }
  if (item.deviceReleasedBy) parts.push(`by ${item.deviceReleasedBy}`);
  return parts.join(' · ') || 'Device is with the customer';
}
